/** @format */

import {
  Alert,
  Box,
  FormHelperText,
  Grid,
  TextField
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { FeedbackDto } from "../../../../common/models/requests/FeedbackDto";
import { addGuardianThunk, selectApplicant } from "../../ApplicantSlice";
import { DependantsDto, LegalGuardianDetailDto, LegalGuardianDetailPostDto } from "../../Models";
import { GetMessageReponse } from "../../../../common/Logic/ServerResponse";
import SavedApplicationPopUp from "../SavedApplicationPopUp";
import MessageResponse from "../../../../common/feedback/messageResponse";
import GuardianForm from "./GuardianForm";
import DisplayGuardians from "./DisplayGuardians";
import AddDependants from "./AddDependants";
import StepperButtons from "../../../../common/custom/StepperButtons";

interface Props {
  setActiveStep: React.Dispatch<React.SetStateAction<number>>;
  activeStep: number;
}

const GUARDIAN_DUPLICATE_MESSAGE =
  "Each guardian must be unique. You cannot save two guardians with the same identity number, guardian type, relationship, and name.";

function normalizeGuardianField(value: string | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

/** Same rules as backend GuardianService.GuardiansContainsDuplicates */
function listHasDuplicateGuardians(
  guardians: LegalGuardianDetailDto[]
): boolean {
  if (!guardians || guardians.length < 2) return false;

  const ids = guardians
    .map((g) => (g.identityNumber ?? "").trim())
    .filter((id) => id.length > 0);
  if (ids.length !== new Set(ids.map((id) => id.toLowerCase())).size)
    return true;

  const keys = new Set<string>();
  for (const g of guardians) {
    const key = `${g.guardianTypeId}|${g.guardianTypeOptionId}|${normalizeGuardianField(g.firstName)}|${normalizeGuardianField(g.surname)}|${normalizeGuardianField(g.identityNumber)}`;
    if (keys.has(key)) return true;
    keys.add(key);
  }
  return false;
}

export default function GuardianDetails({ setActiveStep, activeStep }: Props) {
  const [isSaveAndClose, setIsSaveAndClose] = useState<boolean>(false);
  const dispatch = useAppDispatch();
  const [feedback, setFeedback] = useState<FeedbackDto>({} as FeedbackDto);
  const [maxDependents, setMaxDependents] = useState<number>(0);
  const applicant = useAppSelector(selectApplicant);
  const [formValues, setFormValues] = useState<LegalGuardianDetailPostDto>();
  // Mirrors the backend's IsPrefilledFromPreviousYear flag so we can show an
  // info banner and force a save on Save & Continue. For returning applicants
  // we copy last year's guardians forward; we still need fresh GuardianEntity
  // rows created against the current year, so the dispatch must run even if
  // Formik thinks the values are unchanged.
  const [isPrefilledFromPreviousYear, setIsPrefilledFromPreviousYear] =
    useState<boolean>(false);
  // Index of the guardian currently being edited inline (null when adding a
  // new guardian). Lifted to this parent so DisplayGuardians (the cards) and
  // GuardianForm (the inline form) stay in sync about which row is mid-edit.
  const [editingGuardianIndex, setEditingGuardianIndex] = useState<number | null>(
    null
  );
  const validationSchema = yup.object({
    guardians: yup
      .array()
      .required()
      .min(1, "Please add at least one guardian from the add guardian button")
      .max(2, "Only a maximum of two guardians can be added per application")
      .test(
        "unique-guardians",
        GUARDIAN_DUPLICATE_MESSAGE,
        (arr) =>
          !arr ||
          arr.length < 2 ||
          !listHasDuplicateGuardians(arr as LegalGuardianDetailDto[])
      ),
    dependants: yup
      .array()
      .required()
      .min(
        maxDependents,
        `Please add all (${maxDependents}) required dependents.`
      )
      .max(
        maxDependents,
        `Please add all (${maxDependents}) required dependents.`
      ),
    houseHoldIncomeDepandants: yup
      .number()
      .required("Household income is required")
      .min(0, "Household Income Depandants must be zero or more"),
  });

  const [isSaveAndCloseMessage, setIsSaveAndCloseMessage] =
    useState<string>("");
  const initialValues = {
    guardians: [] as LegalGuardianDetailDto[],
    dependants: [] as DependantsDto[],
    houseHoldIncomeDepandants: 0,
  };
  const formik: FormikProps<LegalGuardianDetailPostDto> =
    useFormik<LegalGuardianDetailPostDto>({
      initialValues: formValues || initialValues,
      validationSchema: validationSchema,
      enableReinitialize: true,
      onSubmit: (values) => {
        // Always dispatch the save. For returning applicants whose form was
        // pre-filled from a previous year Formik will report dirty=false (the
        // values match the pre-fill we set as initial values), but we still
        // need the backend to create fresh GuardianEntity rows for the
        // current year's application.
        dispatch(addGuardianThunk(values)).then((resp: any) => {
          if (
            (resp.type.includes("fulfilled") &&
              resp.payload?.validation.code === 201) ||
            (resp.type.includes("fulfilled") &&
              resp.payload?.validation.code === 200)
          ) {
            setIsPrefilledFromPreviousYear(false);
            if (isSaveAndClose) {
              setIsSaveAndCloseMessage(
                "Your application was saved successfully, you can come back later to complete your application. Press close application to log out and keep your information secure"
              );
            } else {
              setActiveStep(activeStep + 1);
            }
          }
        });
      },
    });

  React.useEffect(() => {
    if (
      applicant.applicantCreateState.status === "failed" &&
      applicant.applicantCreateState.error
    ) {
      const feed = GetMessageReponse(
        applicant.applicantCreateState.error,
        false,
        "fulfilled"
      );
      setFeedback(feed);
      return;
    }
  }, [applicant.applicantCreateState.status]);

  const validationSchemaAddGuardian = yup.object({
    firstName: yup
      .string()
      .required("Guardian First name is required")
      .min(3, "Guardian First name must have at least 3 characters"),
    surname: yup.string().required("Guardian surname is required"),
    identityNumber: yup
      .string()
      .required("Identity number is required")
      .matches(/^[0-9]+$/, "Identity number should contain only digits")
      .matches(
        /(([0-9]{2})(0|1)([0-9])([0-3])([0-9]))([ ]?)(([0-9]{4})([ ]?)([0-1][8]([ ]?)[0-9]))/gm,
        "A valid identity number must be 13 digits"
      )
      .min(13, "A valid identity number must be 13 digits")
      .max(13, "A valid identity number must be 13 digits"),
    contactNumber: yup
      .string()
      .required("Contact Number is required")
      .matches(/^[0-9]+$/, "Phone number should contain only digits")
      .min(10, "A valid contact number must be 10 digits")
      .max(10, "A valid contact number must be 10 digits"),
    guardianTypeId: yup
      .number()
      .required("Guardian Type is required")
      .min(1, "Guardian Type is required"),

    companyName: yup
      .string()
      .nullable()
      .notRequired()
      .when("employed", {
        is: true,
        then: (schema) => schema.required("Company name is required"),
      }),
    occupation: yup
      .string()
      .nullable()
      .notRequired()
      .when("employed", {
        is: true,
        then: (schema) => schema.required("Occupation is required"),
      }),
    companyContact: yup
      .string()
      .nullable()
      .notRequired()
      .when("employed", {
        is: true,
        then: (schema) =>
          schema
            .required("Employer contact is required")
            .min(10, "A valid employer contact must be 10 digits")
            .max(10, "A valid employer contact must be 10 digits"),
      }),

    guardianTypeOptionId: yup
      .number()
      .required("Relationship is required")
      .min(1, "Please select relationship")
      .positive("Relationship is required"),
    employmentStatus: yup
      .number()
      .required("Please Select Employment Status")
      .min(0, "Please Select Employment Status"),
    businessType: yup
      .number()
      .required("Business Type is required")
      .min(0, "Business Type is required"),
    cipcRegistrationNumber: yup
      .string()
      .nullable()
      .notRequired()
      .when("businessType", {
        is: 12,
        then: (schema) =>
          schema.required("CIPC Registration Number is required"),
      }),
    businessName: yup
      .string()
      .nullable()
      .notRequired()
      .when("businessType", {
        is: 12,
        then: (schema) => schema.required("Business Name is required"),
      }),
  });
  const blankGuardian: LegalGuardianDetailDto = {
    companyName: "",
    contactNumber: "",
    employmentStatus: -1,
    firstName: "",
    occupation: "",
    surname: "",
    guardianTypeId: 0,
    companyContact: "",
    doTheyContribute: false,
    identityNumber: "",
    guardianTypeOptionId: 0,
    employed: false,
    businessType: -1,
    cipcRegistrationNumber: "",
    businessName: "",
  };

  const formikAddGuardian: FormikProps<LegalGuardianDetailDto> =
    useFormik<LegalGuardianDetailDto>({
      initialValues: blankGuardian,
      validationSchema: validationSchemaAddGuardian,
      onSubmit: () => {
        const guardians = [...formik.values.guardians];
        if (editingGuardianIndex !== null && editingGuardianIndex >= 0) {
          // Updating an existing guardian (e.g. one prefilled from last year).
          // Preserve the original record's id so the backend can map it back
          // to the existing GuardianEntity row instead of treating it as new.
          const original = guardians[editingGuardianIndex];
          guardians[editingGuardianIndex] = {
            ...formikAddGuardian.values,
            id: original?.id,
            dependants: original?.dependants,
          };
          setEditingGuardianIndex(null);
        } else {
          guardians.push(formikAddGuardian.values);
        }
        if (listHasDuplicateGuardians(guardians)) {
          formik.setFieldError("guardians", GUARDIAN_DUPLICATE_MESSAGE);
          formik.setFieldTouched("guardians", true, false);
          return;
        }
        formik.setFieldError("guardians", undefined);
        formik.setFieldValue("guardians", guardians);
        formikAddGuardian.resetForm({ values: blankGuardian });
      },
    });

  // Loads a guardian's existing values into the inline form so the user can
  // amend them. Marks the row as "being edited" so the form's submit handler
  // replaces in place (instead of appending) and the delete button on the
  // matching card is disabled (we don't want to drop below min 1 mid-edit).
  const handleEditGuardian = (index: number) => {
    const target = formik.values.guardians[index];
    if (!target) return;
    formikAddGuardian.resetForm({ values: { ...blankGuardian, ...target } });
    setEditingGuardianIndex(index);
  };

  const handleCancelEditGuardian = () => {
    formikAddGuardian.resetForm({ values: blankGuardian });
    setEditingGuardianIndex(null);
  };

  const loadGuardians = () => {
    const progress = applicant.loginState.accessToken;
    if (progress && progress.application && progress.application.guardian) {
      setFormValues({
        guardians: progress.application.guardian.guardians,
        dependants: progress.application.guardian.dependants,
        houseHoldIncomeDepandants:
          progress.application.guardian.houseHoldIncomeDepandants,
      } as LegalGuardianDetailPostDto);
      setIsPrefilledFromPreviousYear(
        (progress.application.guardian as any)
          ?.isPrefilledFromPreviousYear === true
      );
    }
  };

  useEffect(() => {
    loadGuardians();
  }, [applicant.loginState.accessToken]);

  useEffect(() => {
    setMaxDependents(formik.values.houseHoldIncomeDepandants);
  }, [formik.values.houseHoldIncomeDepandants]);

  const getGuardianNames = () => {
    if (formik.values.guardians) {
      let bothNames = [] as string[];
      formik.values.guardians.forEach((guard:any) => {
        bothNames.push(guard.firstName);
      });

      return bothNames.join(" and ");
    }
  };
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box className="error_material">
          {formik.touched.guardians &&
            (formik.errors.guardians as React.ReactNode)}
        </Box>
        {isSaveAndCloseMessage && isSaveAndCloseMessage !== "" && (
          <SavedApplicationPopUp
            isSaveAndClose={isSaveAndClose}
            message={isSaveAndCloseMessage}
            status={applicant.applicantCreateState.status}
          />
        )}
        <MessageResponse feedback={feedback}  />
      </Grid>

      {isPrefilledFromPreviousYear && (
        <Grid item xs={12}>
          <Alert severity="info" variant="standard">
            We've pre-filled your guardian / household details from your
            previous application. Please review the guardians, dependants and
            household income figures, and click "Save and Continue" to confirm
            them for this year.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <GuardianForm
          applicant={applicant}
          formik={formik}
          formikAddGuardian={formikAddGuardian}
          isEditing={editingGuardianIndex !== null}
          onCancelEdit={handleCancelEditGuardian}
        />
      </Grid>

      <Grid item xs={12} mt={3}>
        <DisplayGuardians
          formik={formik}
          guardians={formik.values.guardians}
          editingIndex={editingGuardianIndex}
          onEdit={handleEditGuardian}
        />
      </Grid>

      {getGuardianNames() && (
        <Grid item xs={12} mt={3} mb={1}>
          <FormHelperText
            sx={{ color: "green", fontWeight: "500", fontSize: "14px" }}
          >
            {`* Please enter below number of people excluding (${
              applicant.loginState?.accessToken?.displayName
            }) in the household dependent on ${getGuardianNames()}'s income.`}
          </FormHelperText>
        </Grid>
      )}

      {getGuardianNames() && (
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            id="houseHoldIncomeDepandants"
            name="houseHoldIncomeDepandants"
            label={`Enter household dependents on ${getGuardianNames()} income`}
            value={formik.values.houseHoldIncomeDepandants}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            type="text"
            error={
              formik.touched.houseHoldIncomeDepandants &&
              Boolean(formik.errors.houseHoldIncomeDepandants)
            }
            disabled={formik.values.dependants.length > 0}
            helperText={
              formik.touched.houseHoldIncomeDepandants &&
              (formik.errors.houseHoldIncomeDepandants as React.ReactNode)
            }
          />
        </Grid>
      )}

      <Grid item xs={12}>
        <AddDependants formik={formik} />
      </Grid>

      <Grid item xs={12}>
        <Box className="error_material">
          {formik.touched.guardians &&
            (formik.errors.guardians as React.ReactNode)}
        </Box>
        <StepperButtons
          activeStep={activeStep}
          setActiveStep={setActiveStep}
          formik={formik}
          status={applicant.applicantCreateState.status}
          isDisabled={false}
          setSaveAndClose={setIsSaveAndClose}
        />
      </Grid>
    </Grid>
  );
}
